import * as React from 'react'
import { Outlet } from 'react-router-dom'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import Loading from 'modules/core/components/Loading'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import type FiltersForm from 'modules/core/lib/forms/Filters'

import MissingClockInInconsistency from 'modules/attendance/components/ClockIn/Modals/MissingClockInInconsistency'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { TimeTrackingDataProvider } from 'modules/attendance/lib/timeTrackingDataContext'
import type { ClockInStatus } from 'modules/attendance/pages/CustomPeriodsPage'
import type Employee from 'modules/employees/store/models/Employee'

import Content from './Content'
import getFilterValue from './Content/_Table/_filters/getFilterValue'
import Layout from './Layout'
import type CustomPeriodForm from './Layout/form'

interface Props {
  loading: boolean
  customPeriodForm: CustomPeriodForm
  filtersForm: FiltersForm
  hasOvertimeTimeOff: boolean
  canApproveAttendance: boolean
  clockInStatuses: ClockInStatus[]
  canSendEmailReminder: boolean
  showHourDistributionColumn: boolean
  canManageLocation: boolean
  canAccessEditSettings: boolean
}

const TimeTracking = ({
  loading,
  customPeriodForm,
  filtersForm,
  hasOvertimeTimeOff,
  canApproveAttendance,
  clockInStatuses,
  canSendEmailReminder,
  showHourDistributionColumn,
  canManageLocation,
  canAccessEditSettings,
}: Props) => {
  const { filters } = useEmployeeMultiFilters<Employee>(
    getFilterValue,
    LOCAL_STORAGE_KEYS.timeTrackingFilters
  )

  const { featureAvailability: hasInconsistencies } = useAttendanceFeatures(
    Features.time_tracking_time_alerts
  )

  if (loading) {
    const filtersApplied = filters.map((filter) => filter.values.field).join(',')
    const eventProperties = {
      filters_applied: filtersApplied,
      time_period: customPeriodForm.values.period_type,
    }

    track(trackingEvents.timeTracking.goToTimeTrackingView, eventProperties)
  }

  return (
    <TimeTrackingDataProvider>
      <Layout customPeriodForm={customPeriodForm} canAccessEditSettings={canAccessEditSettings}>
        {hasInconsistencies && <MissingClockInInconsistency />}
        {loading && <Loading />}
        {!loading && (
          <Content
            filtersForm={filtersForm}
            customPeriodForm={customPeriodForm}
            hasOvertimeTimeOff={hasOvertimeTimeOff}
            canApproveAttendance={canApproveAttendance}
            clockInStatuses={clockInStatuses}
            canSendEmailReminder={canSendEmailReminder}
            showHourDistributionColumn={showHourDistributionColumn}
            canManageLocation={canManageLocation}
          />
        )}
        {!loading && <Outlet />}
      </Layout>
    </TimeTrackingDataProvider>
  )
}

export default observer(TimeTracking)
